import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  AssetSummary,
  AssetVisibility,
  CustomerAssetsService,
} from '../../@services/customer-assets.service';
import { CustomerDirectoryService } from '../../@services/customer-directory.service';
import { ToastService } from '../../@services/toast.service';
import { environment as env } from '../../../environments/environment';

// Standalone fleet-wide customer assets panel.
// Lists every CustomerAssets row the operator is allowed to see (role
// gated server-side: admin = all; client_admin/member = their customers
// + visibility match). Filter by customer slug + free-text label/filename
// search. Upload a new asset by picking customer + file + visibility
// inline. Each row supports Open / Copy URL / Delete with confirmation;
// delete writes a signed audit entry server-side.
@Component({
  selector: 'app-customer-assets',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './customer-assets.component.html',
})
export class CustomerAssetsComponent implements OnInit {
  assets: AssetSummary[] = [];
  loading = false;
  // Full customer slug list from the Customer Directory (NOT just the ones
  // with existing assets) so the upload form can attach to any customer.
  // Slugs are permanent IDs — operators pick from this list rather than
  // free-text typing.
  allCustomerSlugs: string[] = [];

  // Filters
  filterCustomer = '';
  filterText = '';

  // Upload form
  uploadCustomer = '';
  uploadFile: File | null = null;
  uploadVisAdmin = true;
  uploadVisClientAdmin = true;
  uploadVisMember = true;
  uploadLabel = '';
  uploading = false;

  // Operator's local timezone abbreviation (e.g. "EDT", "PST", "CEST")
  // so the Uploaded column header reads "Uploaded (EDT)" instead of an
  // ambiguous wall-clock timestamp. Computed once on construction —
  // re-rendering the table doesn't recompute. Falls back to UTC offset
  // (e.g. "UTC-04:00") on browsers that don't surface a short name.
  readonly localTzLabel: string = (() => {
    try {
      const parts = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
        .formatToParts(new Date());
      const tz = parts.find(p => p.type === 'timeZoneName')?.value;
      if (tz) return tz;
    } catch { /* fall through */ }
    const offsetMin = -new Date().getTimezoneOffset();
    const sign = offsetMin >= 0 ? '+' : '-';
    const abs = Math.abs(offsetMin);
    const hh = String(Math.floor(abs / 60)).padStart(2, '0');
    const mm = String(abs % 60).padStart(2, '0');
    return `UTC${sign}${hh}:${mm}`;
  })();

  constructor(
    private assetsSvc: CustomerAssetsService,
    private directorySvc: CustomerDirectoryService,
    private toast: ToastService,
  ) {}

  async ngOnInit() {
    await Promise.all([this.reload(), this._loadCustomerSlugs()]);
  }

  private async _loadCustomerSlugs() {
    try {
      const rows = await this.directorySvc.list();
      this.allCustomerSlugs = (rows || [])
        .map((r: any) => String(r?._id || '').trim())
        .filter((s: string) => !!s)
        .sort();
    } catch {
      this.allCustomerSlugs = [];
    }
  }

  async reload() {
    this.loading = true;
    try {
      this.assets = await this.assetsSvc.listAll();
    } catch {
      this.assets = [];
    } finally {
      this.loading = false;
    }
  }

  get distinctCustomers(): string[] {
    const set = new Set<string>();
    for (const a of this.assets) set.add(a.customer_slug);
    return Array.from(set).sort();
  }

  get filtered(): AssetSummary[] {
    const slug = this.filterCustomer.trim();
    const q = this.filterText.trim().toLowerCase();
    return this.assets.filter((a) => {
      if (slug && a.customer_slug !== slug) return false;
      if (q) {
        const hay = `${a.label} ${a.filename} ${a.category}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }

  serveUrl(a: AssetSummary): string {
    return `${env.apiUrl}/rest/admin/customer-assets/${a.customer_slug}/${a.id}`;
  }

  async copyUrl(a: AssetSummary) {
    try {
      await navigator.clipboard.writeText(this.serveUrl(a));
      this.toast.success('Copied', 'Asset URL copied to clipboard');
    } catch {
      this.toast.error('Copy failed', 'Clipboard write was rejected');
    }
  }

  iconClass(a: AssetSummary): string {
    if (a.content_type === 'text/html') return 'fa-globe';
    if (a.content_type === 'application/pdf') return 'fa-file-pdf';
    if (a.content_type === 'text/markdown' || a.filename.endsWith('.md')) return 'fa-file-lines';
    return 'fa-file';
  }

  onFilePicked(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const file = input.files && input.files[0];
    this.uploadFile = file || null;
    if (file && !this.uploadLabel) this.uploadLabel = file.name;
  }

  visibilityChips(): AssetVisibility[] {
    const out: AssetVisibility[] = [];
    if (this.uploadVisAdmin) out.push('admin');
    if (this.uploadVisClientAdmin) out.push('client_admin');
    if (this.uploadVisMember) out.push('member');
    return out;
  }

  async upload() {
    if (!this.uploadCustomer.trim()) {
      this.toast.error('Customer required', 'Pick a customer slug for the asset.');
      return;
    }
    if (!this.uploadFile) {
      this.toast.error('File required', 'Choose a file to upload.');
      return;
    }
    const visibility = this.visibilityChips();
    if (!visibility.length) {
      this.toast.error('Visibility required', 'Tick at least one role.');
      return;
    }
    const MAX = 700 * 1024;
    if (this.uploadFile.size > MAX) {
      this.toast.error('File too large', `${this.uploadFile.name} is ${this.uploadFile.size} bytes; cap is ${MAX} bytes (~700KB).`);
      return;
    }
    this.uploading = true;
    try {
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const dataUrl = String(reader.result || '');
          const idx = dataUrl.indexOf(',');
          resolve(idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl);
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(this.uploadFile!);
      });
      const res = await this.assetsSvc.upload({
        customer_slug: this.uploadCustomer.trim(),
        filename: this.uploadFile.name,
        content_type: this.uploadFile.type || 'application/octet-stream',
        label: this.uploadLabel.trim() || this.uploadFile.name,
        category: 'sso-integration',
        visibility,
        bytes_base64: base64,
      });
      if (res && !('err' in res)) {
        this.assets = [res, ...this.assets];
        this.uploadFile = null;
        this.uploadLabel = '';
      }
    } catch (e: any) {
      this.toast.error('Upload failed', e?.message || 'Unknown error');
    } finally {
      this.uploading = false;
    }
  }

  async deleteAsset(a: AssetSummary) {
    if (!confirm(`Delete "${a.label || a.filename}" for ${a.customer_slug}? An audit entry records the removal.`)) return;
    const res = await this.assetsSvc.delete(a.id);
    if (!res?.ok) return;
    this.assets = this.assets.filter((x) => x.id !== a.id);
  }

  trackById(_: number, a: AssetSummary) {
    return a.id;
  }
}
